/**
 * AffiliateSignupPage — public-facing, tenant-brandable affiliate signup form.
 * Fetches /form-config for brand color + logo, then POSTs to /api/v1/affiliate-signup.
 * Client-side validation on name, email, website; success panel after submit.
 */
import { useMemo, useState, type FormEvent } from "react";
import {
	useAffiliateSignupCreate,
	useAffiliateSignupFormConfig,
} from "./hooks";

const CARD =
	"rounded-[8px] border border-zinc-200 bg-white p-6 shadow-sm";
const LABEL = "text-sm font-medium text-zinc-700";
const INPUT =
	"w-full rounded-[6px] border border-zinc-300 bg-white px-3 py-2 text-sm text-zinc-900 placeholder:text-zinc-400 focus:border-sky-500 focus:outline-none focus:ring-2 focus:ring-sky-500/20";
const BTN_PRIMARY =
	"inline-flex items-center justify-center rounded-[6px] border border-transparent bg-sky-600 px-4 py-2 text-sm font-medium text-white hover:bg-sky-700 disabled:cursor-not-allowed disabled:opacity-60";
const ERROR_TEXT = "text-xs text-red-600";

const TRAFFIC_SOURCE_OPTIONS = [
	{ value: "blog", label: "Blog / content site" },
	{ value: "social", label: "Social media" },
	{ value: "email", label: "Email newsletter" },
	{ value: "youtube", label: "YouTube / video" },
	{ value: "podcast", label: "Podcast" },
	{ value: "paid", label: "Paid ads" },
	{ value: "other", label: "Other" },
] as const;

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const URL_RE = /^https?:\/\/[^\s]+$/i;

interface FormErrors {
	name?: string;
	email?: string;
	website?: string;
}

function validate(fields: {
	name: string;
	email: string;
	website: string;
}): FormErrors {
	const errors: FormErrors = {};
	if (!fields.name.trim()) {
		errors.name = "Name is required";
	} else if (fields.name.trim().length > 255) {
		errors.name = "Name must be 255 characters or fewer";
	}
	if (!fields.email.trim()) {
		errors.email = "Email is required";
	} else if (!EMAIL_RE.test(fields.email.trim())) {
		errors.email = "Enter a valid email";
	}
	if (fields.website.trim() && !URL_RE.test(fields.website.trim())) {
		errors.website = "Use a full URL, e.g. https://example.com";
	}
	return errors;
}

export function AffiliateSignupPage() {
	const configQ = useAffiliateSignupFormConfig();
	const createMut = useAffiliateSignupCreate();

	const [name, setName] = useState("");
	const [email, setEmail] = useState("");
	const [company, setCompany] = useState("");
	const [website, setWebsite] = useState("");
	const [trafficSource, setTrafficSource] = useState("");
	const [referrer, setReferrer] = useState("");
	const [showErrors, setShowErrors] = useState(false);
	const [successId, setSuccessId] = useState<string | null>(null);

	const errors = useMemo(
		() => validate({ name, email, website }),
		[name, email, website],
	);
	const hasErrors = Object.keys(errors).length > 0;

	const brandColor = configQ.data?.branding.brand_color ?? "#0ea5e9";
	const logoUrl = configQ.data?.branding.logo_url ?? null;
	const tenantName = configQ.data?.tenant.name ?? null;

	const handleSubmit = (e: FormEvent) => {
		e.preventDefault();
		setShowErrors(true);
		if (hasErrors) return;

		const body = {
			name: name.trim(),
			email: email.trim(),
			...(company.trim() ? { company: company.trim() } : {}),
			...(website.trim() ? { website: website.trim() } : {}),
			...(trafficSource ? { traffic_sources: [trafficSource] } : {}),
			...(referrer.trim() ? { referrer: referrer.trim() } : {}),
		};

		createMut.mutate(body, {
			onSuccess: (row) => {
				setSuccessId(row.id);
			},
		});
	};

	const resetForm = () => {
		setName("");
		setEmail("");
		setCompany("");
		setWebsite("");
		setTrafficSource("");
		setReferrer("");
		setShowErrors(false);
		setSuccessId(null);
		createMut.reset();
	};

	if (successId) {
		return (
			<main
				data-testid="affiliate-signup-page"
				className="mx-auto flex w-full max-w-xl flex-col gap-8 px-6 py-10"
			>
				<section
					data-testid="affiliate-signup-success"
					className={`${CARD} flex flex-col gap-4`}
				>
					<h1 className="bg-gradient-to-r from-emerald-600 to-sky-600 bg-clip-text text-2xl font-semibold text-transparent">
						Application received
					</h1>
					<p className="text-sm text-zinc-700">
						Thanks for applying to the
						{tenantName ? ` ${tenantName}` : ""} affiliate programme.
						We will review your details and follow up by email.
					</p>
					<p className="text-xs text-zinc-500">
						Reference:{" "}
						<span className="font-mono text-zinc-700">{successId}</span>
					</p>
					<div>
						<button
							type="button"
							onClick={resetForm}
							data-testid="affiliate-signup-again"
							className={BTN_PRIMARY}
						>
							Submit another application
						</button>
					</div>
				</section>
			</main>
		);
	}

	return (
		<main
			data-testid="affiliate-signup-page"
			className="mx-auto flex w-full max-w-xl flex-col gap-8 px-6 py-10"
		>
			<header className="flex flex-col gap-3">
				{logoUrl ? (
					<img
						src={logoUrl}
						alt={tenantName ? `${tenantName} logo` : "Logo"}
						className="h-10 w-auto"
						data-testid="affiliate-signup-logo"
					/>
				) : null}
				<h1
					className="bg-gradient-to-r from-emerald-600 to-sky-600 bg-clip-text text-3xl font-semibold text-transparent"
					style={{ backgroundImage: `linear-gradient(to right, #10b981, ${brandColor})` }}
				>
					Become an affiliate
				</h1>
				<p className="text-sm text-zinc-600">
					Apply to join
					{tenantName ? ` ${tenantName}` : " the"} affiliate programme.
					We review every application and respond within a few business days.
				</p>
			</header>

			<form
				onSubmit={handleSubmit}
				noValidate
				className={`${CARD} flex flex-col gap-5`}
				data-testid="affiliate-signup-form"
				aria-label="affiliate signup form"
			>
				<div className="flex flex-col gap-1.5">
					<label htmlFor="affiliate-signup-name" className={LABEL}>
						Your name <span className="text-red-600">*</span>
					</label>
					<input
						id="affiliate-signup-name"
						type="text"
						autoComplete="name"
						required
						className={INPUT}
						value={name}
						onChange={(e) => setName(e.currentTarget.value)}
						aria-invalid={showErrors && Boolean(errors.name)}
						aria-describedby={errors.name ? "affiliate-signup-name-err" : undefined}
						data-testid="affiliate-signup-name-input"
					/>
					{showErrors && errors.name ? (
						<p
							id="affiliate-signup-name-err"
							data-testid="affiliate-signup-name-err"
							className={ERROR_TEXT}
						>
							{errors.name}
						</p>
					) : null}
				</div>

				<div className="flex flex-col gap-1.5">
					<label htmlFor="affiliate-signup-email" className={LABEL}>
						Email <span className="text-red-600">*</span>
					</label>
					<input
						id="affiliate-signup-email"
						type="email"
						autoComplete="email"
						required
						className={INPUT}
						value={email}
						onChange={(e) => setEmail(e.currentTarget.value)}
						aria-invalid={showErrors && Boolean(errors.email)}
						aria-describedby={errors.email ? "affiliate-signup-email-err" : undefined}
						data-testid="affiliate-signup-email-input"
					/>
					{showErrors && errors.email ? (
						<p
							id="affiliate-signup-email-err"
							data-testid="affiliate-signup-email-err"
							className={ERROR_TEXT}
						>
							{errors.email}
						</p>
					) : null}
				</div>

				<div className="flex flex-col gap-1.5">
					<label htmlFor="affiliate-signup-company" className={LABEL}>
						Company <span className="text-zinc-400">(optional)</span>
					</label>
					<input
						id="affiliate-signup-company"
						type="text"
						autoComplete="organization"
						className={INPUT}
						value={company}
						onChange={(e) => setCompany(e.currentTarget.value)}
						data-testid="affiliate-signup-company-input"
					/>
				</div>

				<div className="flex flex-col gap-1.5">
					<label htmlFor="affiliate-signup-website" className={LABEL}>
						Website <span className="text-zinc-400">(optional)</span>
					</label>
					<input
						id="affiliate-signup-website"
						type="url"
						autoComplete="url"
						placeholder="https://"
						className={INPUT}
						value={website}
						onChange={(e) => setWebsite(e.currentTarget.value)}
						aria-invalid={showErrors && Boolean(errors.website)}
						aria-describedby={errors.website ? "affiliate-signup-website-err" : undefined}
						data-testid="affiliate-signup-website-input"
					/>
					{showErrors && errors.website ? (
						<p
							id="affiliate-signup-website-err"
							data-testid="affiliate-signup-website-err"
							className={ERROR_TEXT}
						>
							{errors.website}
						</p>
					) : null}
				</div>

				<div className="flex flex-col gap-1.5">
					<label htmlFor="affiliate-signup-traffic" className={LABEL}>
						Primary traffic source
					</label>
					<select
						id="affiliate-signup-traffic"
						className={INPUT}
						value={trafficSource}
						onChange={(e) => setTrafficSource(e.currentTarget.value)}
						data-testid="affiliate-signup-traffic-input"
					>
						<option value="">Select one…</option>
						{TRAFFIC_SOURCE_OPTIONS.map((opt) => (
							<option key={opt.value} value={opt.value}>
								{opt.label}
							</option>
						))}
					</select>
				</div>

				<div className="flex flex-col gap-1.5">
					<label htmlFor="affiliate-signup-referrer" className={LABEL}>
						How did you hear about us?{" "}
						<span className="text-zinc-400">(optional)</span>
					</label>
					<input
						id="affiliate-signup-referrer"
						type="text"
						className={INPUT}
						value={referrer}
						onChange={(e) => setReferrer(e.currentTarget.value)}
						data-testid="affiliate-signup-referrer-input"
					/>
				</div>

				<div className="flex items-center gap-3 pt-2">
					<button
						type="submit"
						className={BTN_PRIMARY}
						disabled={createMut.isPending}
						data-testid="affiliate-signup-save"
					>
						{createMut.isPending ? "Submitting…" : "Submit application"}
					</button>
					{createMut.error ? (
						<p
							className="text-sm text-red-600"
							role="alert"
							data-testid="affiliate-signup-error"
						>
							{createMut.error.message}
						</p>
					) : null}
				</div>
			</form>
		</main>
	);
}

export default AffiliateSignupPage;
